import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Waves, Mountain, Star, Flame } from 'lucide-react';

const faqs = [
  {
    q: "What makes Rudragarh different from other Jaisalmer resorts?",
    a: "Rudragarh is a boutique property — not a hotel chain. Every accommodation is distinct: the subterranean Catacomb Suite, water-facing Cascade Cottages, two-level Heritage Huts, and the wood-scented Cocoon. You get private sand dunes, a natural pond and waterfall on the property, and a roster of included desert experiences. The scale is intimate, the service is personal, and the setting is one-of-a-kind."
  },
  {
    q: "How far is Rudragarh from Jaisalmer city?",
    a: "Rudragarh is located near Sam Sand Dunes, approximately 40–45 km from Jaisalmer city centre. The drive takes around 45 minutes on the Jaisalmer–Sam road. We can arrange transfers from Jaisalmer railway station or airport — contact us when booking."
  },
  {
    q: "What desert experiences are included with a stay?",
    a: "All guests have access to the resort's private sand dunes, natural pond and waterfall, swimming pool, gym, cultural programs, open-air theatre, and kids' play zone. Adventure activities — ATV rides on the dunes, horse riding, camel safari, archery, football, and air gun shooting — are available on-site. Rain dance and trekking tracks complete the picture."
  }
];

const highlights = [
  { icon: <Mountain size={28} />, title: "Private Sand Dunes", desc: "Your own stretch of Thar — no crowds, no tours. Just dunes at dawn and starlit skies at night." },
  { icon: <Flame size={28} />, title: "The Catacomb Suite", desc: "Sleep inside carved rock walls with natural climate control. Rudragarh's cave cottages are unlike anything else in Rajasthan." },
  { icon: <Waves size={28} />, title: "Natural Pond & Waterfall", desc: "A rare desert oasis on the property — the natural pond and waterfall are a genuine surprise." },
  { icon: <Star size={28} />, title: "Cultural Immersion", desc: "Evening cultural programs, live kitchen, traditional low-sitting hall — Rajasthan, unfiltered." }
];

const rooms = [
  { slug: "catacomb-suite", name: "The Catacomb Suite", price: 10000, guests: 4, category: "unique", desc: "Rudragarh's most iconic room — carved into rock, naturally cool, with two double beds and a private attached bathroom." },
  { slug: "cascade-cottages", name: "Cascade Cottages", price: 12000, guests: 2, category: "heritage", desc: "Wake up to cascade pond and waterfall views from a private water-facing cottage with a traditional Jharoka window seat." },
  { slug: "heritage-huts", name: "The Heritage Huts", price: 15000, guests: 4, category: "unique", desc: "Two levels of eco-chic bamboo — open kitchen, private porch, and desert air." }
];

const DesertResort = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Desert Resort in Jaisalmer | Rudragarh — Cave Stays & Heritage Cottages</title>
        <meta name="description" content="Rudragarh is a boutique desert resort in Jaisalmer with cave stays, heritage pond-view cottages, private sand dunes, pool, and cultural experiences near Sam Sand Dunes." />
        <meta property="og:title" content="Desert Resort in Jaisalmer | Rudragarh — Cave Stays & Heritage Cottages" />
        <meta property="og:description" content="Rudragarh is a boutique desert resort in Jaisalmer with cave stays, heritage pond-view cottages, private sand dunes, pool, and cultural experiences near Sam Sand Dunes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/jaisalmer/desert-resort" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pond.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Desert Resort in Jaisalmer | Rudragarh" />
        <meta name="twitter:description" content="Rudragarh is a boutique desert resort in Jaisalmer with cave stays, heritage cottages, private dunes, pool, and cultural experiences." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pond.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/jaisalmer/desert-resort" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pond.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer, Rajasthan</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Desert Resort in <span className="text-gold-500">Jaisalmer</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Private dunes, a pool in the sand, cave rooms, and heritage cottages — Rudragarh is the desert resort Jaisalmer deserves.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">The Retreat</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Not a hotel. A <span className="text-gold-500">desert destination.</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              Most Jaisalmer resorts offer a standardised desert experience — a camel ride, a tent, a buffet dinner. Rudragarh was built differently. Set on private land with its own sand dunes, a natural pond and waterfall, and accommodations that range from a carved cave room to heritage pond-view cottages, it's a place where the desert becomes your backdrop, not just your backdrop photo.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              The property sits near Sam Sand Dunes — one of Rajasthan's most spectacular stretches of golden desert — yet feels entirely removed from the bustle. Guests move between the pool, the open-air theatre, the live kitchen, and the private dunes at their own pace. Everything included. Nothing to chase.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              Six distinct accommodation types. Eighteen facilities on-site. One intimate, boutique property. That's Rudragarh.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">What Sets Us Apart</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              The Rudragarh <span className="text-gold-500">Difference</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Where You'll Stay</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms Unlike <span className="text-gold-500">Anywhere Else</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              From subterranean cave stays to stilted bamboo huts and heritage cottages — each room at Rudragarh is its own story.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              About Rudragarh <span className="text-gold-500">Desert Resort</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-hut-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Reserve Your Stay</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            The Desert Awaits
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Book your stay at Rudragarh — one of Jaisalmer's most distinctive desert resorts — and step into the Thar like never before.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default DesertResort;
